import React, {useEffect, useMemo, useState} from 'react';
import {Box} from '@mui/material';
import {
  Body,
  Button,
  Icon,
  designSystemToken,
} from '@lightricks/react-design-system';
import {getApprovalBreakdown} from '@/utils/approvalStatusUtils';
import translate from '@/utils/translate';
import emailValidator from '@/utils/validators/email';
import {
  ContentSubmission,
  ContentSubmissionVersion,
  CONTENT_SUBMISSION_STATUS,
} from '@/types/contentSubmission';
import useCreateCommentMutation from '@/hooks/mutations/use-create-comment';
import useCreateOffPlatformApprovalMutation from '@/hooks/mutations/use-create-off-platform-approval';
import useContentSubmissionOffPlatformReviewStore, {
  UserIdentity,
  contentSubmissionOffPlatformReviewActions,
} from '@/stores/contentSubmissionOffPlatformReviewStore';
import CommentInput from './CommentInput';
import CommenterIdentityForm from './CommenterIdentityForm';
import styles from './OffPlatformReviewFooter.module.scss';

interface OffPlatformReviewFooterProps {
  contentSubmission: ContentSubmission;
  version: ContentSubmissionVersion;
}

function OffPlatformReviewFooter({
  contentSubmission,
  version,
}: OffPlatformReviewFooterProps) {
  const userIdentity = useContentSubmissionOffPlatformReviewStore(
    (s) => s.userIdentity
  );
  const pendingIdentity = useContentSubmissionOffPlatformReviewStore(
    (s) => s.pendingIdentity
  );
  const isEditingIdentity = useContentSubmissionOffPlatformReviewStore(
    (s) => s.isEditingIdentity
  );
  const effectiveIdentity = pendingIdentity ?? userIdentity;
  const {setUserIdentity, setPendingIdentity, setIsEditingIdentity} =
    contentSubmissionOffPlatformReviewActions;

  const [commentText, setCommentText] = useState('');
  const [formValues, setFormValues] = useState<UserIdentity>({
    name: '',
    email: '',
    role: '',
  });

  const prevIsEditing = React.useRef(isEditingIdentity);
  useEffect(
    function handleEditToggle() {
      const wasEditing = prevIsEditing.current;
      prevIsEditing.current = isEditingIdentity;

      if (isEditingIdentity && !wasEditing && userIdentity) {
        setFormValues({
          name: userIdentity.name,
          email: userIdentity.email,
          role: userIdentity.role,
        });
      }

      // Commit valid edits to identity when the form closes
      if (!isEditingIdentity && wasEditing) {
        const trimmedName = formValues.name.trim();
        const trimmedEmail = formValues.email.trim();
        if (
          trimmedName !== '' &&
          emailValidator(trimmedEmail) &&
          formValues.role !== ''
        ) {
          setUserIdentity({
            name: trimmedName,
            email: trimmedEmail,
            role: formValues.role,
          });
        }
      }
    },
    // Only fire on isEditingIdentity transitions — ref tracks previous value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isEditingIdentity]
  );

  const approvalSteps = contentSubmission.campaign?.campaignApprovalSteps ?? [];
  const hasApprovalSteps = approvalSteps.length > 0;

  const hasValidIdentity = hasApprovalSteps
    ? !!userIdentity?.role
    : !!userIdentity?.name;
  const shouldShowIdentityForm = !hasValidIdentity || isEditingIdentity;

  const isFormValid =
    formValues.name.trim() !== '' &&
    emailValidator(formValues.email) &&
    (!hasApprovalSteps || formValues.role !== '');

  const isFooterEnabled = shouldShowIdentityForm
    ? isFormValid
    : hasValidIdentity;

  useEffect(
    function syncPendingFromForm() {
      if (shouldShowIdentityForm) {
        setPendingIdentity(
          isFormValid
            ? {
                name: formValues.name.trim(),
                email: formValues.email.trim(),
                role: formValues.role,
              }
            : null
        );
      } else {
        setPendingIdentity(null);
      }
    },
    [shouldShowIdentityForm, isFormValid, formValues, setPendingIdentity]
  );

  const {approvedSteps, requiredStepsNames} = useMemo(() => {
    const breakdown = getApprovalBreakdown(contentSubmission.approvalStatus);
    return {
      approvedSteps: new Set(breakdown.approvedStepsNames),
      requiredStepsNames: breakdown.requiredStepsNames,
    };
  }, [contentSubmission.approvalStatus]);

  const selectedRole = shouldShowIdentityForm
    ? formValues.role
    : effectiveIdentity?.role;
  const roleAlreadyApproved = !!selectedRole && approvedSteps.has(selectedRole);
  const isLatestVersion = version.id === contentSubmission.latestVersionId;
  const latestVersion = isLatestVersion
    ? version
    : (contentSubmission.versions?.find(
        (v) => v.id === contentSubmission.latestVersionId
      ) ?? version);

  const {mutate: createComment, isLoading: isSubmittingComment} =
    useCreateCommentMutation({contentSubmissionId: contentSubmission.id});
  const {mutate: createApproval, isLoading: isApproving} =
    useCreateOffPlatformApprovalMutation({
      contentSubmissionId: contentSubmission.id,
    });

  const commitIdentityIfNeeded = () => {
    const identity = effectiveIdentity;
    if (!identity) return;
    if (!userIdentity || isEditingIdentity) {
      setUserIdentity(identity);
    }
    setIsEditingIdentity(false);
  };

  const handleSubmitComment = () => {
    const trimmed = commentText.trim();
    if (!trimmed || isSubmittingComment || !isFooterEnabled) return;

    const identity = effectiveIdentity!;
    commitIdentityIfNeeded();

    createComment(
      {
        content: trimmed,
        email: identity.email,
        fullName: identity.name,
        step: identity.role.toLowerCase(),
        contentSubmissionVersion: {
          id: latestVersion.id,
          contentSubmissionComments:
            latestVersion.contentSubmissionComments ?? [],
        },
      },
      {
        // The mutation invalidates the content-submission query, so the
        // submitted comment appears via refetch in the aggregated feed.
        onSuccess: () => {
          setCommentText('');
        },
      }
    );
  };

  const handleApproveClick = () => {
    if (roleAlreadyApproved || isApproving || !isFooterEnabled) return;

    const identity = effectiveIdentity!;
    commitIdentityIfNeeded();

    // The mutation invalidates the content-submission query, so the approval
    // appears via refetch in the aggregated feed.
    createApproval({
      contentSubmissionId: contentSubmission.id,
      contentSubmissionVersionId: version.id,
      step: identity.role.toLowerCase(),
      email: identity.email,
    });
  };

  const allStepsApproved =
    requiredStepsNames.length > 0 &&
    requiredStepsNames.every((s) => approvedSteps.has(s));

  const isPending =
    contentSubmission.aasmState === CONTENT_SUBMISSION_STATUS.PENDING;
  const isAlreadyApproved = roleAlreadyApproved || allStepsApproved;
  const isApproveDisabled =
    !isFooterEnabled ||
    isAlreadyApproved ||
    isApproving ||
    !isLatestVersion ||
    !isPending;

  const approveButtonLabel: string = isAlreadyApproved
    ? translate('Approved')
    : translate('Approve');

  return (
    <Box className={styles.offPlatformFooter}>
      {shouldShowIdentityForm ? (
        <>
          <CommenterIdentityForm
            values={formValues}
            onChange={setFormValues}
            contentSubmission={contentSubmission}
            approvedSteps={approvedSteps}
          />
          <Box className={styles.formDivider} />
        </>
      ) : null}

      <CommentInput
        value={commentText}
        onChange={setCommentText}
        onSubmit={handleSubmitComment}
        disabled={!isFooterEnabled}
        isSubmitting={isSubmittingComment}
        placeholder={
          isLatestVersion
            ? translate('Add a comment...')
            : translate('Add a comment on latest version...')
        }
      />

      {hasApprovalSteps ? (
        <>
          <Box className={styles.orDivider}>
            <Box className={styles.orDividerLine} />
            <Body size="xs" color={designSystemToken('semantic.fg.tertiary')}>
              {translate('OR')}
            </Body>
            <Box className={styles.orDividerLine} />
          </Box>

          <Button
            appearance="neutral"
            mode="filled"
            size="large"
            disabled={isApproveDisabled}
            className={styles.approveButton}
            startIcon={
              <Icon
                name="Actions-Patch-Accept-Line"
                size="medium"
                appearance={isApproveDisabled ? 'disabled' : 'inverse'}
              />
            }
            onClick={handleApproveClick}
          >
            {approveButtonLabel}
          </Button>
        </>
      ) : null}
    </Box>
  );
}

export default OffPlatformReviewFooter;
